export type TextScale = 100 | 115 | 130
export type ContrastMode = 'STANDARD' | 'HIGH'
export type RenderQuality = 'HIGH' | 'PERFORMANCE'

export interface GameSettingsState {
  readonly textScale: TextScale
  readonly contrast: ContrastMode
  readonly reducedMotion: boolean
  readonly volume: number
  readonly renderQuality: RenderQuality
}

const STORAGE_KEY = 'block-defense.settings.v1'

const DEFAULT_SETTINGS: GameSettingsState = {
  textScale: 100,
  contrast: 'STANDARD',
  reducedMotion: false,
  volume: 0.75,
  renderQuality: 'HIGH',
}

class GameSettings {
  private state = this.load()
  private listeners = new Set<(settings: GameSettingsState) => void>()

  get snapshot(): GameSettingsState { return this.state }

  update(patch: Partial<GameSettingsState>): GameSettingsState {
    this.state = normalizeSettings({ ...this.state, ...patch })
    this.save()
    this.listeners.forEach((listener) => listener(this.state))
    return this.state
  }

  reset(): GameSettingsState { return this.update(DEFAULT_SETTINGS) }

  subscribe(listener: (settings: GameSettingsState) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private load(): GameSettingsState {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY)
      return saved ? normalizeSettings(JSON.parse(saved)) : DEFAULT_SETTINGS
    } catch {
      return DEFAULT_SETTINGS
    }
  }

  private save(): void {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state)) } catch { /* storage can be unavailable */ }
  }
}

function normalizeSettings(value: unknown): GameSettingsState {
  const input = value && typeof value === 'object' ? value as Partial<GameSettingsState> : {}
  const textScale = input.textScale === 115 || input.textScale === 130 ? input.textScale : 100
  const contrast = input.contrast === 'HIGH' ? 'HIGH' : 'STANDARD'
  const volume = typeof input.volume === 'number' && Number.isFinite(input.volume) ? Math.min(1, Math.max(0, input.volume)) : DEFAULT_SETTINGS.volume
  const renderQuality = input.renderQuality === 'PERFORMANCE' ? 'PERFORMANCE' : 'HIGH'
  return { textScale, contrast, reducedMotion: input.reducedMotion === true, volume, renderQuality }
}

export const gameSettings = new GameSettings()
